import { Scrollable } from 'components/atoms';
import { Product } from 'components/molecules/Goods/Product';
import renderIcon from 'icon';
import QUERY_KEYS from 'network/config/queryKeys';
import { getProductsBySellerSubcategoryNameAlt } from 'network/services/goods';
import { useMemo, useState } from 'react';
import { IoArrowBackCircleSharp } from 'react-icons/io5';
import { useInfiniteQuery } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Add search icon components
const SearchIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ProductsBySubCategory = () => {
  const { category, vendor, sub_category } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Extract seller details and current category from location state
  const { seller, parentCategoryId, prefetchedProducts, branchId } = (location.state || {}) as {
    seller?: { businessName?: string };
    parentCategoryId?: number;
    prefetchedProducts?: Array<Record<string, unknown>>;
    branchId?: number;
  };

  const subCategoryLabel = sub_category ? decodeURIComponent(sub_category) : '';
  const resolvedParentCategoryId =
    parentCategoryId ?? (category && /^\d+$/.test(category) ? Number(category) : undefined);

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [viewMode] = useState('grid');
  const LIMIT = 20;

  // Debug logging to verify params
  console.log('Route params:', { category, vendor, sub_category });

  // Get products for this category with infinite scroll using your API
  const {
    data: productsData,
    isLoading: productsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: productsError
  } = useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.PRODUCTS_BY_SELLER_SUBCATEGORY,
      {
        sellerId: vendor,
        subCategoryName: sub_category,
        parentCategoryId: resolvedParentCategoryId,
        branchId
      }
    ],
    enabled: !!(sub_category && vendor),
    queryFn: ({ pageParam = 0 }) =>
      getProductsBySellerSubcategoryNameAlt({
        sellerId: vendor!,
        subCategoryName: sub_category!,
        parentCategoryId: resolvedParentCategoryId,
        branchId,
        page: pageParam,
        size: LIMIT
      }),
    getNextPageParam: lastPage => {
      if (lastPage && typeof lastPage === 'object') {
        const { currentPage, totalPages } = lastPage;
        return currentPage < totalPages - 1 ? currentPage + 1 : undefined;
      }
      return undefined;
    },
    staleTime: 2 * 60 * 1000
  });

  // Get all products and apply search filter
  const allProducts = useMemo(() => {
    const fromApi =
      productsData?.pages?.flatMap(page => (page && Array.isArray(page.data) ? page.data : [])) ?? [];

    if (fromApi.length > 0) {
      return fromApi;
    }

    return Array.isArray(prefetchedProducts) ? prefetchedProducts : [];
  }, [productsData?.pages, prefetchedProducts]);

  const filteredProducts = useMemo(() => {
    let products = allProducts;

    // Apply search filter if there's a search query
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      products = allProducts.filter(
        product =>
          product.productName?.toLowerCase().includes(searchLower) ||
          product.barCode?.toLowerCase().includes(searchLower) ||
          product.description?.toLowerCase().includes(searchLower)
      );
    }

    // Remove duplicates based on productId
    const uniqueProducts = products.filter((product, index, self) =>
      index === self.findIndex(p => p.productId === product.productId)
    );

    // Sort products: in-stock items first, out-of-stock items last
    return uniqueProducts.sort((a, b) => {
      // If both are in stock or both are out of stock, maintain original order
      if ((a.quantityAvailable > 0 && b.quantityAvailable > 0) ||
        (a.quantityAvailable === 0 && b.quantityAvailable === 0)) {
        return 0;
      }
      // If a is in stock and b is out of stock, a comes first
      if (a.quantityAvailable > 0 && b.quantityAvailable === 0) {
        return -1;
      }
      // If a is out of stock and b is in stock, b comes first
      return 1;
    });
  }, [allProducts, searchQuery]);

  // Navigate to the previous page
  const handleBack = () => {
    navigate(-1);
  };

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Search functionality handlers
  const handleSearchIconClick = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchClose = () => {
    setIsSearchExpanded(false);
    setSearchQuery('');
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Search functionality is handled by onChange
  };

  if (productsError) {
    console.error('Products error:', productsError);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading products</p>
          <p className="text-gray-600 text-sm mb-4">{(productsError as any) || 'Something went wrong'}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const isInitialLoading = productsLoading;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-1 lg:px-6 py-6">
          {isInitialLoading ? (
            <div className="animate-pulse">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-gray-300 h-6 w-6 rounded"></div>
                <div className="bg-gray-300 h-8 w-48 rounded"></div>
              </div>
              <div className="bg-gray-300 h-6 w-64 rounded mb-2"></div>
              <div className="bg-gray-300 h-4 w-96 rounded"></div>
            </div>
          ) : (
            <>
              {/* Breadcrumb Navigation */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  {renderIcon(IoArrowBackCircleSharp, {
                    size: 24,
                    className: 'text-black flex-shrink-0 cursor-pointer hover:text-gray-600 transition-colors',
                    onClick: handleBack
                  })}
                  <span>{seller?.businessName || `Vendor ${vendor}`}</span>
                  <span>›</span>
                  <span>{subCategoryLabel}</span>
                </div>

                {/* Search Section */}
                <div className="flex items-center">
                  {!isSearchExpanded ? (
                    <button
                      onClick={handleSearchIconClick}
                      className="text-black hover:text-gray-600 transition-colors p-1">
                      <SearchIcon />
                    </button>
                  ) : (
                    <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
                      <div className="relative">
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={handleSearchInputChange}
                          placeholder="Search products..."
                          className="w-48 sm:w-64 px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          autoFocus
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <SearchIcon />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleSearchClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors p-1">
                        <CloseIcon />
                      </button>
                    </form>
                  )}
                </div>
              </div>

              {/* Vendor & Category Info */}
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{subCategoryLabel || 'Products'}</h1>
              </div>
              {/* Search Bar */}
            </>
          )}
        </div>
      </div>

      {/* Products Content */}
      <div className="max-w-7xl mx-auto  lg:px-6 ">
        {isInitialLoading ? (
          <div
            className={`grid gap-1 ${viewMode === 'grid'
              ? 'grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
              : 'grid-cols-1 sm:grid-cols-3 lg:grid-cols-2'
              }`}>
            {[...Array(8)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-gray-300 h-48 w-full rounded-lg mb-3"></div>
                <div className="bg-gray-300 h-4 w-3/4 rounded mb-2"></div>
                <div className="bg-gray-300 h-4 w-1/2 rounded mb-2"></div>
                <div className="bg-gray-300 h-6 w-1/3 rounded"></div>
              </div>
            ))}
          </div>
        ) : filteredProducts?.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">{searchQuery ? '🔍' : '📦'}</div>
            <p className="text-gray-600 text-lg mb-2">
              {searchQuery
                ? `No products found for "${searchQuery}"`
                : `No products available in ${subCategoryLabel || 'this category'}`}
            </p>
            <p className="text-gray-500 text-sm">
              {searchQuery ? 'Try adjusting your search terms' : 'Check back later for new products'}
            </p>
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="mt-4 text-blue-600 hover:text-blue-700">
                Clear search
              </button>
            )}
          </div>
        ) : (
          <Scrollable onScrollBottom={handleLoadMore} callWhen={hasNextPage}>
            <div
              className={`grid gap-6 ${viewMode === 'grid'
                ? 'grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                : 'grid-cols-1 sm:grid-cols-3 lg:grid-cols-2'
                }`}>
              {filteredProducts?.map(product => (
                <div key={product.productId}>
                  <Product
                    product={{
                      ...product,
                      seller: product?.seller,
                      status: product.status ?? '',
                      description: product.description ?? ''
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Loading indicator for infinite scroll */}
            {isFetchingNextPage && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-500 mt-2">Loading more products...</p>
              </div>
            )}
          </Scrollable>
        )}
      </div>
    </div>
  );
};

export default ProductsBySubCategory;
